import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ChevronRight, User, FileText, CreditCard, AlertCircle, Eye, Calendar, CheckCircle2, LogOut } from 'lucide-react';

// TaxSetu Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

/**
 * GST Portal Dashboard - Shown after successful login
 * Mimics the real GST portal dashboard with Returns Calendar
 */
const GSTPortalDashboard = ({ companyData, onNavigate, onLogout, formType = 'GSTR-1' }) => {
  const navigate = useNavigate();
  const currentDate = new Date();
  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }).replace(/\//g, '/');
  };

  // Generate last 5 return periods - auto-updates based on current date
  // Logic: GST returns are filed for the PREVIOUS month. So latest period in calendar = previous month.
  // Earlier 4 periods are already filed; latest one is "To be Filed".
  const getReturnPeriods = () => {
    const periods = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Start from 5 months ago and go to PREVIOUS month (i=1), skipping the current month entirely
    for (let i = 5; i >= 1; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const isLatest = i === 1; // Previous month is the latest filing period
      periods.push({
        month: months[date.getMonth()],
        year: date.getFullYear(),
        isCurrent: isLatest,
        gstr1Status: isLatest ? 'To be Filed' : 'Filed',
        gstr3bStatus: isLatest ? 'To be Filed' : 'Filed'
      });
    }
    return periods;
  };

  // Compute current filing quarter label (based on previous month, which is the active filing period)
  const getFilingQuarterLabel = () => {
    const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const m = prevDate.getMonth(); // 0-11
    const y = prevDate.getFullYear();
    if (m >= 3 && m <= 5) return `Apr-Jun ${y}`;
    if (m >= 6 && m <= 8) return `Jul-Sep ${y}`;
    if (m >= 9 && m <= 11) return `Oct-Dec ${y}`;
    return `Jan-Mar ${y}`; // Jan-Mar
  };
  const filingQuarterLabel = getFilingQuarterLabel();

  const returnPeriods = getReturnPeriods();

  const handleGoToReturn = () => {
    onNavigate();
  };

  // Handle logout - clear session and go back to email entry
  const handleLogout = () => {
    // Clear all GSTR-1 access related data from localStorage
    localStorage.removeItem('gstr1_access_email');
    localStorage.removeItem('gstr1_pending_email');
    localStorage.removeItem('gstr1_access_granted');
    localStorage.removeItem('gstr1_last_request_time');
    localStorage.removeItem('gstr1_last_request_email');
    localStorage.removeItem('pw_batch_email');
    localStorage.removeItem('practiq_lead_email');
    
    // Call parent logout handler if provided
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Skip to Main Content Bar */}
      <div className="bg-[#0b1e3f] text-white text-xs py-1.5 px-4 flex justify-between items-center">
        <button 
          onClick={() => navigate('/gst')}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors border border-white/20"
        >
          <span>← Back</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-gray-300 cursor-pointer hover:underline">Skip to Main Content</span>
          <span className="flex items-center gap-2 text-gray-300">
            <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
            <span className="cursor-pointer">A<sup>+</sup></span>
            <span className="cursor-pointer">A<sup>-</sup></span>
          </span>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#0b1e3f] text-white py-4 px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 flex-shrink-0">
              <img 
                src={TAXSETU_LOGO}
                alt="TaxSetu"
                className="h-full object-contain"
              />
            </div>
            <div className="border-l border-white/30 pl-4">
              <h1 className="text-xl font-normal" style={{ fontFamily: 'Georgia, serif' }}>
                Demo Goods and Services Tax
              </h1>
              <p className="text-sm text-gray-300">
                Practical Simulation - Educational Purpose Only
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4" />
              <span className="max-w-[200px] truncate">{companyData.legalName?.slice(0, 20)}...</span>
            </div>
            <div className="relative">
              <span className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-gray-700">
                🔔
              </span>
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full text-[10px] flex items-center justify-center text-white">0</span>
            </div>
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded text-sm transition-colors border border-red-400/50 text-red-200 hover:text-white ml-2"
              data-testid="gst-logout-btn"
              title="Sign out and change email"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="bg-[#2a5298]">
        <div className="max-w-7xl mx-auto flex items-center">
          <button className="px-4 py-2.5 bg-white/20 text-white font-medium text-sm">Dashboard</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">Services ▾</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">GST Law</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">Downloads ▾</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">Search Taxpayer ▾</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">Help and Taxpayer Facilities</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">e-Invoice</button>
          <button className="px-4 py-2.5 text-white hover:bg-white/10 text-sm">News and Updates</button>
        </div>
      </nav>

      {/* Sub Header */}
      <div className="bg-white border-b py-2 px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-600">
          <span>Dashboard</span>
          <div className="flex items-center gap-4">
            <span>Last logged in on {formatDate(new Date(Date.now() - 86400000))} 09:30</span>
            <span>Currently logged in from IP: 122.177.98.XXX</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-6 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-6">
            {/* Left Content */}
            <div className="flex-1">
              {/* Welcome Message */}
              <div className="text-center mb-6">
                <h2 className="text-xl text-blue-700">
                  Welcome <span className="font-semibold">{companyData.legalName}</span> to GST Common Portal
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Return filing preference ({filingQuarterLabel}) : <span className="font-medium">{companyData.filingPreference || 'Monthly'}</span>
                  <span className="text-blue-600 ml-2 cursor-pointer">(Change)</span>
                </p>
              </div>

              {/* Returns Calendar - Exact GST Portal Style */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
                <h3 className="text-center font-bold text-gray-800 text-xl mb-6">Returns Calendar (Last 5 return periods)</h3>
                
                <div className="overflow-x-auto">
                  {/* GSTR-1 / IFF Row */}
                  <div className="flex mb-1">
                    <div className="w-32 flex-shrink-0 bg-[#1e3a5f] text-white flex items-center justify-center font-semibold py-6 rounded-l">
                      GSTR-1 / IFF
                    </div>
                    {returnPeriods.map((period, idx) => (
                      <div 
                        key={`gstr1-${idx}`}
                        className={`flex-1 min-w-[100px] flex flex-col items-center justify-center py-4 px-2 border-l border-white/20 ${
                          period.gstr1Status === 'Filed' 
                            ? 'bg-[#26a69a]' // Teal-green for Filed
                            : 'bg-[#f5a623]' // Orange/amber for To be Filed (matches real portal)
                        } ${idx === returnPeriods.length - 1 ? 'rounded-r' : ''}`}
                      >
                        <span className="text-white font-semibold text-sm">{period.month} - {period.year}</span>
                        <span className="text-white text-sm mt-1">{period.gstr1Status}</span>
                      </div>
                    ))}
                  </div>

                  {/* GSTR-3B Row */}
                  <div className="flex">
                    <div className="w-32 flex-shrink-0 bg-[#1e3a5f] text-white flex items-center justify-center font-semibold py-6 rounded-l">
                      GSTR-3B
                    </div>
                    {returnPeriods.map((period, idx) => (
                      <div 
                        key={`gstr3b-${idx}`}
                        className={`flex-1 min-w-[100px] flex flex-col items-center justify-center py-4 px-2 border-l border-white/20 border-t border-t-gray-300 ${
                          period.gstr3bStatus === 'Filed' 
                            ? 'bg-[#26a69a]' // Teal-green for Filed (darker)
                            : 'bg-[#f5a623]' // Orange/amber for To be Filed
                        } ${idx === returnPeriods.length - 1 ? 'rounded-r' : ''}`}
                      >
                        <span className="text-white font-semibold text-sm">{period.month} - {period.year}</span>
                        <span className="text-white text-sm mt-1">{period.gstr3bStatus}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-center text-gray-500 text-sm mt-6 italic">
                  You can navigate to your chosen page through navigation panel given below
                </p>
              </div>

              {/* Info Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="italic">
                      A facility is provided to you to Geocode the existing business addresses saved in GST system. 
                      Kindly click on Continue to update the Geocoded Addresses. Please note that the existing addresses 
                      appearing in the GST system/Registration Certificate will not be impacted.
                      <span className="text-blue-600 ml-1 cursor-pointer">Continue ℹ</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button 
                  onClick={handleGoToReturn}
                  className="bg-[#2a5298] hover:bg-[#1a4288] text-white"
                >
                  RETURN DASHBOARD <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button variant="outline" className="border-[#2a5298] text-[#2a5298]">
                  CREATE CHALLAN <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button variant="outline" className="border-[#2a5298] text-[#2a5298]">
                  VIEW NOTICE(S) AND ORDER(S) <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <div className="flex gap-3 mb-8">
                <Button className="bg-[#2a5298] hover:bg-[#1a4288] text-white">
                  ANNUAL RETURN <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Continue to Dashboard */}
              <div className="text-center">
                <span className="text-gray-600 mr-2">Else Go to »</span>
                <Button 
                  onClick={handleGoToReturn}
                  className="bg-[#2a5298] hover:bg-[#1a4288] text-white"
                >
                  CONTINUE TO {formType} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="w-64 flex-shrink-0">
              {/* Company Info Card */}
              <div className="bg-white border rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-2">{companyData.legalName}</h4>
                <p className="text-xs text-gray-600 font-mono mb-3">{companyData.gstin}</p>
                <a href="#" className="text-blue-600 text-sm flex items-center gap-1 hover:underline">
                  View Profile <Eye className="w-3 h-3" />
                </a>
              </div>

              {/* Quick Links */}
              <div className="bg-white border rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 text-sm mb-3 text-center border-b pb-2">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="#" className="text-blue-600 hover:underline">Check Cash Balance</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">Liability ledger</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">Credit ledger</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">Electronic Credit Reversal and Re-claimed Statement</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">Negative Liability Statement - Regular Taxpayers</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">RCM Liability/ITC Statement</a></li>
                  <li><a href="#" className="text-blue-600 hover:underline">Application for Unbarring Returns</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a5298] text-white py-3 px-8 mt-auto">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
          <span>© 2025-26 Goods and Services Tax Network (Demo)</span>
          <span>Site Last Updated on {formatDate(currentDate)}</span>
          <span>Designed & Developed by PractiQ</span>
        </div>
      </footer>
    </div>
  );
};

export default GSTPortalDashboard;
